import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, toLegacyCursorPage } from '../../services/apiClient';
import AppLoadingView from './AppLoadingView';
import { markPageEnter } from '../../utils/aos';

// Bản build đã vẽ sẵn màn hình tải trong HTML (scripts/inline-app-loading.js, ngoài #root) để lần vẽ đầu
// không phải chờ JS. React không vẽ lại bản đó (vẽ lại tạo phần tử mới, LCP lại phải chờ JS),
// chỉ gắn class mờ dần rồi gỡ đi. Lúc dev chưa có bản vẽ sẵn thì tự render AppLoadingView.
const getPrerenderedLoading = () => (
  typeof document !== 'undefined' ? document.getElementById('app-loading') : null
);

// Chờ font tối đa bấy nhiêu ms kể từ lúc AppLoading mount, mạng chậm thì hiện trang với font hệ thống rồi đổi sau
const FONT_WAIT_MS = 2500;

const AppLoading = ({ onLoadingComplete, ready = true }) => {
  const [showLoading, setShowLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  // Đọc một lần lúc render đầu, trước khi React tự render AppLoadingView (cũng mang id app-loading)
  const [prerendered] = useState(getPrerenderedLoading);
  const { user } = useAuth();

  // Gọi API orders và notifications khi AppLoading mount và có user
  useEffect(() => {
    (async () => {
      try {
        if (!user || !user.id) return;
        
        const token = localStorage.getItem('token') || localStorage.getItem('test_token');
        if (!token) return;
        
        // ========== Gọi API Orders ==========
        const ordersStorageKey = `orders_data_${user.id}`;

        // Kiểm tra cache trong localStorage trước
        const storedData = localStorage.getItem(ordersStorageKey);
        let shouldCallOrdersAPI = false;
        
        if (storedData) {
          try {
            const cachedData = JSON.parse(storedData);
            const cacheExpiryTime = 5 * 60 * 1000; // 5 phút
            const now = Date.now();
            const cacheTimestamp = cachedData.timestamp || 0;
            
            if (!cacheTimestamp || (now - cacheTimestamp) >= cacheExpiryTime) {
              // Cache không tồn tại hoặc đã hết hạn, cần gọi API
              shouldCallOrdersAPI = true;
              localStorage.removeItem(ordersStorageKey);
            }
          } catch (error) {
            // Nếu parse JSON lỗi, xóa cache và gọi API
            console.error('Error parsing cached orders data:', error);
            localStorage.removeItem(ordersStorageKey);
            shouldCallOrdersAPI = true;
          }
        } else {
          // Không có cache, cần gọi API
          shouldCallOrdersAPI = true;
        }
        
        // Gọi API Orders nếu cần
        if (shouldCallOrdersAPI) {
          let data = null;
          try {
            data = toLegacyCursorPage(await api.get('/me/orders', { query: { limit: 10 }, onUnauthorized: 'throw' }));
          } catch (_) {
            data = null;
          }

          if (data) {
            
            // Lưu vào localStorage với timestamp
            const dataWithTimestamp = {
              ...data,
              timestamp: Date.now()
            };
            localStorage.setItem(ordersStorageKey, JSON.stringify(dataWithTimestamp));
          }
        }

        // ========== Gọi API Notifications ==========
        // Gọi API unread-count
        try {
          const { data: unreadData } = await api.get('/me/notifications/unread-count', { onUnauthorized: 'throw' });
          const unreadCountKey = `notifications_unread_count_${user.id}`;
          localStorage.setItem(unreadCountKey, String(unreadData?.count || 0));
        } catch (error) {
          console.error('AppLoading: Error fetching unread count:', error);
        }

        // Gọi API notifications list
        try {
          const { data: notifications } = await api.get('/me/notifications', { onUnauthorized: 'throw' });
          const notificationsKey = `notifications_data_${user.id}`;
          const notificationsWithTimestamp = {
            notifications: notifications || [],
            timestamp: Date.now()
          };
          localStorage.setItem(notificationsKey, JSON.stringify(notificationsWithTimestamp));
        } catch (error) {
          console.error('AppLoading: Error fetching notifications:', error);
        }
      } catch (error) {
        console.error('AppLoading: Error fetching data:', error);
      }
    })();
  }, [user]);

  useEffect(() => {
    let timer;
    const scheduleCompletion = () => {
      const startedAt = prerendered?.getAttribute('data-loading-started-at');
      const elapsed = startedAt !== null && startedAt !== undefined
        ? Math.max(0, performance.now() - Number(startedAt))
        : 0;
      // Count animation time already spent in the prerendered HTML.
      // React and route readiness must never restart the 1400ms timeline.
      clearTimeout(timer);
      timer = setTimeout(() => setAnimationComplete(true), Math.max(0, 1400 - elapsed));
    };
    if (prerendered?.classList.contains('is-prerendered')) {
      prerendered.addEventListener('app-loading-start', scheduleCompletion, { once: true });
    } else {
      scheduleCompletion();
    }
    return () => {
      clearTimeout(timer);
      prerendered?.removeEventListener('app-loading-start', scheduleCompletion);
    };
  }, [prerendered]);

  useEffect(() => {
    // Route loading and the unchanged SVG animation run in parallel.
    if (ready && animationComplete && fontsReady) setIsExiting(true);
  }, [ready, animationComplete, fontsReady]);

  // Tải sẵn Be Vietnam Pro trong lúc chờ chunk trang và chỉ mờ màn hình tải khi font đã về (tối đa FONT_WAIT_MS
  // kể từ lúc mount). Trang hiện ra trước khi font về thì đổi font layout lại cả trang (~300ms trên CPU chậm)
  // và vẽ lại h1 trang chủ, PageSpeed lấy đó làm LCP. Chữ mẫu có Đ, ổ, ề để tải đủ subset latin, latin-ext, vietnamese
  useEffect(() => {
    if (!document.fonts?.load) {
      setFontsReady(true);
      return undefined;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setFontsReady(true);
    };
    const timer = setTimeout(finish, FONT_WAIT_MS);
    Promise.all(['400', '600', '700', '800'].map(weight => (
      document.fonts.load(`${weight} 16px "Be Vietnam Pro"`, 'Đổi tiền').catch(() => {})
    ))).then(finish);
    return () => {
      done = true;
      clearTimeout(timer);
    };
  }, []);

  // Ẩn trang bên dưới đến lúc mờ dần (FastPageTransition.css). Bản build đã gắn sẵn trong index.html
  useEffect(() => {
    document.documentElement.classList.toggle('app-loading-hold', !isExiting);
    // Trang vừa hiện ra: khối có data-aos trong màn hình đầu hiện cùng trang, không chạy hiệu ứng riêng
    if (isExiting) markPageEnter();
    return () => document.documentElement.classList.remove('app-loading-hold');
  }, [isExiting]);

  useEffect(() => {
    if (prerendered) prerendered.classList.toggle('is-exiting', isExiting);
    if (!isExiting) return undefined;
    // Start removal only after React has committed the fade class.
    const timer = setTimeout(() => {
      setShowLoading(false);
      onLoadingComplete?.();
    }, 300);
    return () => clearTimeout(timer);
  }, [isExiting, onLoadingComplete, prerendered]);

  // Remove the prerendered overlay after the fade completes.
  useEffect(() => {
    if (prerendered && !showLoading) prerendered.remove();
  }, [prerendered, showLoading]);
  useEffect(() => () => prerendered?.remove(), [prerendered]);

  if (!showLoading || prerendered) {
    return null;
  }

  return <AppLoadingView isExiting={isExiting} />;
};

export default AppLoading;
